/**
 * 고용관리 Hooks (V45 → V45.2)
 *
 * 고용관리 페이지에서 사용하는 데이터 조회 및 mutation hooks
 *
 * **기능:**
 * - 고용관리 목록 조회 (탭별: 검색/계약만료임박/예약정보/히스토리)
 * - 퇴사 예약/변경/취소 (사유 코드/노트 포함)
 * - 계약 연장
 * - 고용형태 변경 (예약 방식)
 *
 * **V45.2 추가:**
 * - 퇴사 사유 코드/노트 필드
 * - 고용형태 변경 예약 필드
 * - 실행 후 잠금 (isLocked)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

// ============================================
// V45.5: Firebase 에러 메시지 추출 헬퍼
// ============================================

/**
 * Firebase 에러에서 사용자 친화적 메시지 추출
 *
 * Firebase callable function 에러 구조:
 * - err.message: "INTERNAL" 또는 실제 메시지
 * - err.code: "functions/internal" 등
 * - err.details: 추가 정보 (있을 경우)
 *
 * HttpsError에서 설정한 메시지가 err.message에 있지만,
 * 일부 에러 코드(internal, unknown)의 경우 원본 메시지가 숨겨질 수 있음
 */
function extractFirebaseErrorMessage(err: any, fallbackMessage: string): string {
  // 1. err.message가 있고, 유효한 경우 (단순 코드명이 아닌 경우)
  if (err.message && typeof err.message === 'string') {
    const msg = err.message;
    // Firebase 코드명만 있는 경우 (INTERNAL, PERMISSION_DENIED 등) 제외
    const codePatterns = ['INTERNAL', 'PERMISSION_DENIED', 'UNAUTHENTICATED', 'NOT_FOUND', 'INVALID_ARGUMENT'];
    const isJustCode = codePatterns.some(code => msg.toUpperCase() === code);
    if (!isJustCode && msg.length > 0) {
      return msg;
    }
  }

  // 2. err.details?.message 확인
  if (err.details?.message) {
    return err.details.message;
  }

  // 3. err.code에서 의미 추출
  if (err.code) {
    const code = err.code.replace('functions/', '');
    switch (code) {
      case 'unauthenticated':
        return '로그인이 필요합니다.';
      case 'permission-denied':
        return '권한이 없습니다.';
      case 'not-found':
        return '요청한 리소스를 찾을 수 없습니다.';
      case 'invalid-argument':
        return '입력값이 올바르지 않습니다.';
      case 'failed-precondition':
        return '요청 조건을 충족하지 않습니다.';
      case 'internal':
        return '서버 내부 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
      default:
        break;
    }
  }

  // 4. 기본 메시지
  return fallbackMessage;
}

// ============================================
// 타입 정의
// ============================================

/** 탭 종류 (V45.2 업데이트) */
export type EmploymentManagementTab = 'search' | 'expiring' | 'reservations' | 'history';

/** 고용형태 코드 */
export type EmploymentTypeCode = 'DISPATCH' | 'INTERN' | 'CONTRACT' | 'REGULAR';

/** V45.2: 퇴사 사유 코드 */
export type TerminationReasonCode =
  | 'VOLUNTARY'
  | 'CONTRACT_EXPIRED'
  | 'RECOMMENDED_RESIGNATION'
  | 'DISCIPLINARY_DISMISSAL'
  | 'OTHER';

/** V45.2: 퇴사 사유 코드 → 한글 매핑 */
export const TERMINATION_REASON_NAMES: Record<TerminationReasonCode, string> = {
  VOLUNTARY: '자발적 퇴사',
  CONTRACT_EXPIRED: '계약 만료',
  RECOMMENDED_RESIGNATION: '권고 사직',
  DISCIPLINARY_DISMISSAL: '징계 해고',
  OTHER: '기타',
};

/** 필터 */
export interface EmploymentManagementFilters {
  employmentTypeCode?: EmploymentTypeCode;
  orgUnitId?: string;
  searchQuery?: string;
}

/** 목록 항목 (V45.2 → V45.3 → V46 업데이트) */
export interface EmploymentManagementItem {
  historyId: string;
  employeeId: string;
  employeeName: string;
  employeeNumber?: string; // V45.2
  email?: string; // V45.2
  orgUnitId?: string;
  orgUnitName?: string;
  employmentTypeCode: EmploymentTypeCode;
  employmentTypeName: string;
  startDate: string;
  endDate?: string;
  leaveDate?: string; // V46: users.leaveDate (퇴사일)
  firstHireDate?: string; // V304: users.firstHireDate (최초입사일)
  terminationPlanned: boolean;
  terminationPlannedEndDate?: string;
  terminationPlannedRunDate?: string; // V254: 배치 실행일
  terminationPlannedAt?: string;
  terminationPlannedBy?: string;
  terminationReasonCode?: TerminationReasonCode; // V45.2
  terminationReasonNote?: string; // V45.2
  terminationExecutedAt?: string; // V45.2
  employmentTypeChangePlanned?: boolean; // V45.2
  employmentTypeChangePlannedEffectiveDate?: string; // V45.2
  employmentTypeChangePlannedRunDate?: string; // V254: 배치 실행일
  employmentTypeChangeTargetCode?: EmploymentTypeCode; // V45.2
  employmentTypeChangeTargetEndDate?: string; // V45.7
  // V45.3: 계약 연장 예약 필드
  contractExtensionPlanned?: boolean;
  contractExtensionPlannedEffectiveDate?: string;
  contractExtensionPlannedRunDate?: string; // V254: 배치 실행일
  contractExtensionPlannedNewEndDate?: string;
  daysUntilEndDate?: number;
  daysUntilTermination?: number;
  status: 'normal' | 'expiring' | 'scheduled' | 'typeChangePlanned' | 'extensionPlanned'; // V45.3
  isLocked: boolean; // V45.2
}

/** 목록 조회 응답 */
interface GetEmploymentManagementListResponse {
  success: boolean;
  items: EmploymentManagementItem[];
  totalCount: number;
}

/** 공통 응답 */
interface OperationResponse {
  success: boolean;
  message: string;
}

// ============================================
// useEmploymentManagementList Hook
// ============================================

export interface UseEmploymentManagementListResult {
  items: EmploymentManagementItem[];
  totalCount: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * 고용관리 목록 조회 Hook
 *
 * @param tab 탭 종류 (all, expiring, scheduled)
 * @param filters 필터 조건
 * @param options.searchTriggerCount V46: 직원검색 탭 전용 - 검색 버튼 클릭 시 증가하는 카운터
 *
 * V75: requestId 패턴으로 race condition 해결
 * - 탭 전환 시 이전 요청 응답이 현재 탭 데이터를 덮어쓰는 문제 수정
 *
 * V82: search 탭 전환 시 race condition 추가 수정
 * - expiring→search 빠른 전환 시 expiring 응답이 search 탭 데이터를 덮어쓰는 버그 수정
 * - search 탭 전환 시에도 fetchData 호출하여 requestId 증가 및 items 초기화
 */
export function useEmploymentManagementList(
  tab: EmploymentManagementTab,
  filters?: EmploymentManagementFilters,
  options?: { searchTriggerCount?: number }
): UseEmploymentManagementListResult {
  const [items, setItems] = useState<EmploymentManagementItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false); // V46: 초기값 false로 변경
  const [error, setError] = useState<Error | null>(null);

  // V46: 직원검색 탭에서 필터 변경 시 자동 조회 방지를 위해 ref 사용
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const tabRef = useRef(tab);
  tabRef.current = tab;

  // V75: requestId 패턴으로 race condition 방지
  // 각 요청에 고유 ID를 부여하고, 응답 시 현재 활성 requestId와 일치하는지 확인
  const requestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    const currentTab = tabRef.current;
    const currentFilters = filtersRef.current;

    if (!functions) {
      setItems([]);
      setTotalCount(0);
      setIsLoading(false);
      return;
    }

    // V46: 직원검색 탭에서는 searchTriggerCount가 0보다 클 때만 조회
    // 다른 탭은 기존처럼 자동 조회
    if (currentTab === 'search' && (!options?.searchTriggerCount || options.searchTriggerCount === 0)) {
      // V82: 탭 전환 시 이전 요청 응답 무효화 (requestId 증가)
      // 이를 통해 expiring→search 빠른 전환 시 expiring 응답이 search 탭 데이터를 덮어쓰는 문제 방지
      const newRequestId = ++requestIdRef.current;
      setItems([]);
      setTotalCount(0);
      setIsLoading(false);
      return;
    }

    // V75: 새 요청 ID 생성 (이전 요청 무효화)
    const thisRequestId = ++requestIdRef.current;

    try {
      setIsLoading(true);
      setError(null);

      const getList = httpsCallable<
        { tab: EmploymentManagementTab; filters?: EmploymentManagementFilters },
        GetEmploymentManagementListResponse
      >(functions, 'getEmploymentManagementList');

      const result = await getList({ tab: currentTab, filters: currentFilters });

      // V75: stale response 무시 - 요청 후 탭이 변경되었으면 setState 하지 않음
      if (thisRequestId !== requestIdRef.current) {
        return;
      }

      if (result.data.success) {
        setItems(result.data.items);
        setTotalCount(result.data.totalCount);
      } else {
        throw new Error('목록 조회 실패');
      }
    } catch (err: any) {
      // V75: 에러 발생 시에도 stale response 무시
      if (thisRequestId !== requestIdRef.current) {
        return;
      }
      console.error('[V75] ❌ 고용관리 목록 조회 실패:', err);
      setError(err);
      setItems([]);
      setTotalCount(0);
    } finally {
      // V75: 현재 활성 요청인 경우에만 loading 해제
      if (thisRequestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [options?.searchTriggerCount]);

  // V46 → V82: 탭 변경 또는 필터 변경 시 자동 조회
  // V82: 모든 탭에서 fetchData 호출 (search 탭은 내부에서 requestId 증가 및 items 초기화 후 early return)
  // 이를 통해 expiring→search 빠른 전환 시 이전 요청이 무효화됨
  useEffect(() => {
    fetchData();
  }, [tab, filters?.employmentTypeCode, filters?.orgUnitId, filters?.searchQuery, fetchData]);

  // V46: 직원검색 탭에서 검색 버튼 클릭 시에만 조회
  useEffect(() => {
    if (tab === 'search' && options?.searchTriggerCount && options.searchTriggerCount > 0) {
      fetchData();
    }
  }, [tab, options?.searchTriggerCount, fetchData]);

  return {
    items,
    totalCount,
    isLoading,
    error,
    refetch: fetchData,
  };
}

// ============================================
// Mutation Hooks
// ============================================

export interface MutationState {
  isLoading: boolean;
  error: Error | null;
}

/**
 * 퇴사 예약 등록 Mutation Hook (V45.2 업데이트)
 */
export function useScheduleTermination() {
  const [state, setState] = useState<MutationState>({ isLoading: false, error: null });

  const mutate = async (
    historyId: string,
    plannedEndDate: string,
    terminationReasonCode: TerminationReasonCode,
    terminationReasonNote?: string,
    reason?: string, // 하위 호환성
    plannedRunDate?: string // V254: 배치 실행일
  ): Promise<OperationResponse> => {
    if (!functions) {
      throw new Error('Firebase Functions not initialized');
    }

    try {
      setState({ isLoading: true, error: null });

      const scheduleTermination = httpsCallable<
        {
          historyId: string;
          plannedEndDate: string;
          terminationReasonCode: TerminationReasonCode;
          terminationReasonNote?: string;
          reason?: string;
          plannedRunDate?: string;
        },
        OperationResponse
      >(functions, 'scheduleTermination');

      const result = await scheduleTermination({
        historyId,
        plannedEndDate,
        terminationReasonCode,
        terminationReasonNote,
        reason,
        plannedRunDate,
      });

      setState({ isLoading: false, error: null });
      return result.data;
    } catch (err: any) {
      console.error('퇴사 예약 등록 실패:', err);
      setState({ isLoading: false, error: err });
      throw err;
    }
  };

  return { ...state, mutate };
}

/**
 * 퇴사 예약 날짜 변경 Mutation Hook
 */
export function useChangeTerminationDate() {
  const [state, setState] = useState<MutationState>({ isLoading: false, error: null });

  const mutate = async (
    historyId: string,
    newPlannedEndDate: string,
    plannedRunDate?: string // V254: 배치 실행일
  ): Promise<OperationResponse> => {
    if (!functions) {
      throw new Error('Firebase Functions not initialized');
    }

    try {
      setState({ isLoading: true, error: null });

      const changeTerminationDate = httpsCallable<
        { historyId: string; newPlannedEndDate: string; plannedRunDate?: string },
        OperationResponse
      >(functions, 'changeTerminationDate');

      const result = await changeTerminationDate({ historyId, newPlannedEndDate, plannedRunDate });

      setState({ isLoading: false, error: null });
      return result.data;
    } catch (err: any) {
      console.error('퇴사 예약 날짜 변경 실패:', err);
      setState({ isLoading: false, error: err });
      throw err;
    }
  };

  return { ...state, mutate };
}

/**
 * 퇴사 예약 취소 Mutation Hook
 */
export function useCancelTermination() {
  const [state, setState] = useState<MutationState>({ isLoading: false, error: null });

  const mutate = async (historyId: string): Promise<OperationResponse> => {
    if (!functions) {
      throw new Error('Firebase Functions not initialized');
    }

    try {
      setState({ isLoading: true, error: null });

      const cancelTermination = httpsCallable<{ historyId: string }, OperationResponse>(
        functions,
        'cancelTermination'
      );

      const result = await cancelTermination({ historyId });

      setState({ isLoading: false, error: null });
      return result.data;
    } catch (err: any) {
      console.error('퇴사 예약 취소 실패:', err);
      setState({ isLoading: false, error: err });
      throw err;
    }
  };

  return { ...state, mutate };
}

/**
 * 계약 연장 Mutation Hook
 */
export function useExtendContract() {
  const [state, setState] = useState<MutationState>({ isLoading: false, error: null });

  const mutate = async (historyId: string, newEndDate: string): Promise<OperationResponse> => {
    if (!functions) {
      throw new Error('Firebase Functions not initialized');
    }

    try {
      setState({ isLoading: true, error: null });

      const extendContract = httpsCallable<
        { historyId: string; newEndDate: string },
        OperationResponse
      >(functions, 'extendContract');

      const result = await extendContract({ historyId, newEndDate });

      setState({ isLoading: false, error: null });
      return result.data;
    } catch (err: any) {
      console.error('계약 연장 실패:', err);
      setState({ isLoading: false, error: err });
      throw err;
    }
  };

  return { ...state, mutate };
}

/**
 * 고용형태 변경 예약 Mutation Hook (V45.2 → V45.4 → V61: endDate, conversionBonusYears 추가)
 */
export function useScheduleEmploymentTypeChange() {
  const [state, setState] = useState<MutationState>({ isLoading: false, error: null });

  const mutate = async (
    historyId: string,
    newTypeCode: EmploymentTypeCode,
    effectiveDate: string,
    reason?: string,
    endDate?: string, // V45.4: 파견/인턴/계약직일 때 필수
    conversionBonusYears?: number, // V61: 정규직 전환 시 전환가산
    plannedRunDate?: string // V254: 배치 실행일
  ): Promise<OperationResponse> => {
    if (!functions) {
      throw new Error('Firebase Functions not initialized');
    }

    try {
      setState({ isLoading: true, error: null });

      const changeEmploymentType = httpsCallable<
        { historyId: string; newTypeCode: EmploymentTypeCode; effectiveDate: string; reason?: string; endDate?: string; conversionBonusYears?: number; plannedRunDate?: string },
        OperationResponse
      >(functions, 'changeEmploymentType');

      const result = await changeEmploymentType({ historyId, newTypeCode, effectiveDate, reason, endDate, conversionBonusYears, plannedRunDate });

      setState({ isLoading: false, error: null });
      return result.data;
    } catch (err: any) {
      console.error('고용형태 변경 예약 실패:', err);
      setState({ isLoading: false, error: err });
      throw err;
    }
  };

  return { ...state, mutate };
}

/**
 * 고용형태 변경 예약 취소 Mutation Hook (V45.2)
 *
 * 백엔드: cancelEmploymentTypeChange callable function
 */
export function useCancelEmploymentTypeChange() {
  const [state, setState] = useState<MutationState>({ isLoading: false, error: null });

  const mutate = async (historyId: string): Promise<OperationResponse> => {
    if (!functions) {
      throw new Error('Firebase Functions not initialized');
    }

    try {
      setState({ isLoading: true, error: null });

      const cancelEmploymentTypeChange = httpsCallable<{ historyId: string }, OperationResponse>(
        functions,
        'cancelEmploymentTypeChange'
      );

      const result = await cancelEmploymentTypeChange({ historyId });

      setState({ isLoading: false, error: null });
      return result.data;
    } catch (err: any) {
      console.error('고용형태 변경 예약 취소 실패:', err);
      setState({ isLoading: false, error: err });
      throw err;
    }
  };

  return { ...state, mutate };
}

/**
 * 고용형태 변경 (하위 호환성용, V45.2에서는 Schedule 사용 권장)
 */
export function useChangeEmploymentType() {
  return useScheduleEmploymentTypeChange();
}

/**
 * V45.3 → V45.5: 계약 연장 예약 Mutation Hook
 *
 * 백엔드: scheduleContractExtension callable function
 *
 * V45.5: Firebase 에러 메시지 추출 개선
 */
export function useScheduleContractExtension() {
  const [state, setState] = useState<MutationState>({ isLoading: false, error: null });

  const mutate = async (
    historyId: string,
    effectiveDate: string,
    newEndDate: string,
    reason?: string,
    plannedRunDate?: string // V254: 배치 실행일
  ): Promise<OperationResponse> => {
    if (!functions) {
      throw new Error('Firebase Functions not initialized');
    }

    try {
      setState({ isLoading: true, error: null });

      const scheduleContractExtension = httpsCallable<
        { historyId: string; effectiveDate: string; newEndDate: string; reason?: string; plannedRunDate?: string },
        OperationResponse
      >(functions, 'scheduleContractExtension');

      const result = await scheduleContractExtension({ historyId, effectiveDate, newEndDate, reason, plannedRunDate });

      setState({ isLoading: false, error: null });
      return result.data;
    } catch (err: any) {
      console.error('[V46 Debug] ❌ 계약 연장 예약 실패:', err);
      setState({ isLoading: false, error: err });
      // V45.5: Firebase 에러에서 사용자 친화적 메시지 추출
      const errorMessage = extractFirebaseErrorMessage(err, '계약 연장 예약 중 오류가 발생했습니다.');
      const enhancedError = new Error(errorMessage);
      throw enhancedError;
    }
  };

  return { ...state, mutate };
}

/**
 * V45.3 → V45.5: 계약 연장 예약 취소 Mutation Hook
 *
 * 백엔드: cancelContractExtension callable function
 *
 * V45.5: Firebase 에러 메시지 추출 개선
 */
export function useCancelContractExtension() {
  const [state, setState] = useState<MutationState>({ isLoading: false, error: null });

  const mutate = async (historyId: string): Promise<OperationResponse> => {
    if (!functions) {
      throw new Error('Firebase Functions not initialized');
    }

    try {
      setState({ isLoading: true, error: null });

      const cancelContractExtension = httpsCallable<{ historyId: string }, OperationResponse>(
        functions,
        'cancelContractExtension'
      );

      const result = await cancelContractExtension({ historyId });

      setState({ isLoading: false, error: null });
      return result.data;
    } catch (err: any) {
      console.error('계약 연장 예약 취소 실패:', err);
      setState({ isLoading: false, error: err });
      // V45.5: Firebase 에러에서 사용자 친화적 메시지 추출
      const errorMessage = extractFirebaseErrorMessage(err, '계약 연장 예약 취소 중 오류가 발생했습니다.');
      throw new Error(errorMessage);
    }
  };

  return { ...state, mutate };
}

// ============================================
// useEmploymentManagementHistory Hook (V47: 커서 기반 페이지네이션)
// ============================================

/** 히스토리 로그 항목 */
export interface EmploymentManagementHistoryLog {
  id: string;
  actionType: string;
  actionTypeName: string;
  employeeId: string;
  employeeName: string;
  employeeNumber?: string;
  email?: string;
  historyId?: string;
  effectiveDate: string;
  executedAt: string;
  executedBy: string;
  executedByName: string;
  beforeSnapshot?: any;
  afterSnapshot?: any;
  reason?: string;
  companyCode?: string; // V62: 대상 직원의 회사 코드 (시차 표시용)
}

/** 커서 타입 */
export interface HistoryCursor {
  executedAt: string;
  docId: string;
}

/** 히스토리 필터 타입 (searchQuery만 지원) */
export interface HistoryFilters {
  searchQuery?: string;
}

/** 히스토리 조회 결과 (V47: 페이지네이션 지원) */
export interface UseEmploymentManagementHistoryResult {
  logs: EmploymentManagementHistoryLog[];
  totalCount: number;
  isLoading: boolean;
  error: Error | null;
  hasNext: boolean;
  nextCursor?: HistoryCursor;
  /** 첫 페이지 조회 (검색 조건 변경 시) */
  fetch: (startDate: string, endDate: string, filters?: HistoryFilters) => Promise<void>;
  /** 다음 페이지 조회 */
  fetchNext: () => Promise<void>;
  /** 다운로드용 전체 데이터 조회 */
  fetchForExport: (startDate: string, endDate: string, searchQuery?: string) => Promise<EmploymentManagementHistoryLog[]>;
}

/** 히스토리 조회 응답 타입 */
interface HistoryResponse {
  success: boolean;
  logs: EmploymentManagementHistoryLog[];
  totalCount: number;
  nextCursor?: HistoryCursor;
  hasNext: boolean;
}

/** 다운로드 응답 타입 */
interface ExportResponse {
  success: boolean;
  logs: EmploymentManagementHistoryLog[];
  totalCount: number;
}

/**
 * 고용관리 히스토리 조회 Hook (V47: 커서 기반 페이지네이션)
 *
 * **페이지네이션 정책:**
 * - searchQuery 없음: 커서 기반 페이지네이션 (20개씩)
 * - searchQuery 있음: 페이지네이션 비활성, 최대 500건
 */
export function useEmploymentManagementHistory(): UseEmploymentManagementHistoryResult {
  const [logs, setLogs] = useState<EmploymentManagementHistoryLog[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasNext, setHasNext] = useState(false);
  const [nextCursor, setNextCursor] = useState<HistoryCursor | undefined>();

  // 마지막 조회 조건 저장 (다음 페이지 조회용)
  const lastQueryRef = useRef<{ startDate: string; endDate: string; searchQuery?: string } | null>(null);

  const fetch = useCallback(async (
    startDate: string,
    endDate: string,
    filters?: HistoryFilters
  ) => {
    if (!functions) {
      setLogs([]);
      setTotalCount(0);
      setIsLoading(false);
      return;
    }

    if (!startDate || !endDate) {
      setLogs([]);
      setTotalCount(0);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // 조회 조건 저장
      lastQueryRef.current = { startDate, endDate, searchQuery: filters?.searchQuery };

      const getHistory = httpsCallable<
        { startDate: string; endDate: string; searchQuery?: string; pageSize?: number; cursor?: HistoryCursor },
        HistoryResponse
      >(functions, 'getEmploymentManagementHistory');

      const payload = {
        startDate,
        endDate,
        searchQuery: filters?.searchQuery,
        pageSize: 20,
      };

      const result = await getHistory(payload);

      if (result.data.success) {
        setLogs(result.data.logs);
        setTotalCount(result.data.totalCount);
        setHasNext(result.data.hasNext);
        setNextCursor(result.data.nextCursor);
      } else {
        throw new Error('히스토리 조회 실패');
      }
    } catch (err: any) {
      console.error('고용관리 히스토리 조회 실패:', err);
      setError(err);
      setLogs([]);
      setTotalCount(0);
      setHasNext(false);
      setNextCursor(undefined);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchNext = useCallback(async () => {
    if (!functions || !nextCursor || !lastQueryRef.current) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const getHistory = httpsCallable<
        { startDate: string; endDate: string; searchQuery?: string; pageSize?: number; cursor?: HistoryCursor },
        HistoryResponse
      >(functions, 'getEmploymentManagementHistory');

      const { startDate, endDate, searchQuery } = lastQueryRef.current;

      const payload = {
        startDate,
        endDate,
        searchQuery,
        pageSize: 20,
        cursor: nextCursor,
      };

      const result = await getHistory(payload);

      if (result.data.success) {
        // 기존 데이터에 추가
        setLogs(prev => [...prev, ...result.data.logs]);
        setHasNext(result.data.hasNext);
        setNextCursor(result.data.nextCursor);
      } else {
        throw new Error('히스토리 조회 실패');
      }
    } catch (err: any) {
      console.error('다음 페이지 조회 실패:', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [nextCursor]);

  const fetchForExport = useCallback(async (
    startDate: string,
    endDate: string,
    searchQuery?: string
  ): Promise<EmploymentManagementHistoryLog[]> => {
    if (!functions) {
      throw new Error('Firebase Functions not initialized');
    }

    const getExport = httpsCallable<
      { startDate: string; endDate: string; searchQuery?: string },
      ExportResponse
    >(functions, 'getEmploymentManagementHistoryExport');

    const result = await getExport({ startDate, endDate, searchQuery });

    if (result.data.success) {
      return result.data.logs;
    } else {
      throw new Error('히스토리 다운로드 실패');
    }
  }, []);

  return {
    logs,
    totalCount,
    isLoading,
    error,
    hasNext,
    nextCursor,
    fetch,
    fetchNext,
    fetchForExport,
  };
}

// ============================================
// V55.28 → V55.30: 고용형태 변경 수동 트리거 Hook
// ============================================

/** V55.30: 배치 진단 정보 타입 */
export interface BatchDiagnostics {
  processedCount: number;
  matchedByQueryCount: number;
  eligibleByDateCount: number;
  skippedReasonCounts: {
    futureDate: number;
    missingEffectiveDate: number;
    missingTargetCode: number;
    updateFailed: number;
  };
  sampleMatchedIds: string[];
  todayKST: string;
}

/** V55.30: 수동 트리거 응답 타입 (진단 정보 포함) */
export interface ManualTriggerResponse {
  success: boolean;
  processedCount: number;
  message: string;
  diagnostics: BatchDiagnostics;
}

/**
 * V55.28: 고용형태 변경 예약 수동 실행 Hook
 *
 * 관리자가 스케줄러 대신 수동으로 예약된 고용형태 변경을 즉시 실행할 수 있습니다.
 * 백엔드: manualTriggerEmploymentTypeChange callable function
 *
 * **권한:** system_admin, role_hr 또는 AdminLike
 */
export function useManualTriggerEmploymentTypeChange() {
  const [state, setState] = useState<MutationState>({ isLoading: false, error: null });

  const mutate = async (): Promise<ManualTriggerResponse> => {
    if (!functions) {
      throw new Error('Firebase Functions not initialized');
    }

    try {
      setState({ isLoading: true, error: null });

      const manualTrigger = httpsCallable<void, ManualTriggerResponse>(
        functions,
        'manualTriggerEmploymentTypeChange'
      );

      const result = await manualTrigger();

      setState({ isLoading: false, error: null });
      return result.data;
    } catch (err: any) {
      console.error('고용형태 변경 수동 실행 실패:', err);
      setState({ isLoading: false, error: err });
      // Firebase 에러에서 사용자 친화적 메시지 추출
      const errorMessage = extractFirebaseErrorMessage(err, '고용형태 변경 수동 실행 중 오류가 발생했습니다.');
      throw new Error(errorMessage);
    }
  };

  return { ...state, mutate };
}
